import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Badge, AIPanel } from '@/components';
import { Mic, CheckCircle2, Edit2 } from 'lucide-react';
import { useTranscriptStream } from '@/firebase/useTranscriptStream';
import { useSessionStore } from '@/stores/sessionStore';

// TV-P7-06 — map LiveKit participant identity to a display
// label + color. The Cloud Run worker writes participantIdentity
// straight from LiveKit, which mirrors the FHIR resource ref
// ('Practitioner/3', 'Patient/12'). This is a shallow gloss for
// the UI; a future ticket can resolve the real name via the
// FHIR aggregator.
function presentSpeaker(participantIdentity) {
  if (!participantIdentity) {
    return { label: 'Unknown', color: 'text-gray-300', bgColor: 'bg-gray-700/30' };
  }
  if (participantIdentity.startsWith('Practitioner/')) {
    return { label: 'Provider', color: 'text-blue-400', bgColor: 'bg-blue-500/10' };
  }
  if (participantIdentity.startsWith('Patient/')) {
    return { label: 'Patient', color: 'text-green-400', bgColor: 'bg-green-500/10' };
  }
  if (participantIdentity.toLowerCase().includes('guest') ||
      participantIdentity.toLowerCase().includes('interpreter')) {
    return { label: 'Guest', color: 'text-purple-400', bgColor: 'bg-purple-500/10' };
  }
  return { label: participantIdentity, color: 'text-amber-300', bgColor: 'bg-amber-500/10' };
}

function formatChunkTime(chunk) {
  // Prefer server-side receivedAt (FirestoreTimestamp). Fall back
  // to clientTimestamp (number) when interim chunks haven't yet
  // had a server roundtrip.
  const ms = chunk.receivedAt?.toMillis?.()
    ?? chunk.clientTimestamp
    ?? Date.now();
  const d = new Date(ms);
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

const AIDocs = () => {
  // Encounter id comes from the route (/ai-docs/:encounterId) — falls
  // back to the active encounter on sessionStore for direct opens.
  const { encounterId: routeEncId } = useParams();
  const sessionEncId = useSessionStore((s) => s.currentEncounter);
  const encounterId = routeEncId || sessionEncId;

  const [pulseVisible, setPulseVisible] = useState(true);

  // Animate the mic pulse only while STT is actively producing.
  useEffect(() => {
    const interval = setInterval(() => {
      setPulseVisible((prev) => !prev);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // TV-P7-06 — replace the hardcoded transcription[] with the real
  // Firestore stream. Renders interim chunks faded; finals stay solid.
  const { chunks, metadata, isStreaming, error } =
    useTranscriptStream(encounterId);
  const isListening = isStreaming;

  // Collapse interims into finals: when a final lands for the same
  // participantIdentity, drop any prior interims from that participant
  // since the final supersedes them. This keeps the rolling view
  // clean during interactive speech.
  const transcription = useMemo(() => {
    const out = [];
    for (const chunk of chunks) {
      const speaker = presentSpeaker(chunk.participantIdentity);
      if (chunk.isFinal) {
        // Drop any trailing interims from this participant — the
        // final replaces them.
        while (
          out.length &&
          !out[out.length - 1].isFinal &&
          out[out.length - 1].participantIdentity === chunk.participantIdentity
        ) {
          out.pop();
        }
        out.push({
          ...chunk,
          ...speaker,
          timestamp: formatChunkTime(chunk),
        });
      } else {
        // Replace the last interim for this participant, if any,
        // with the new one.
        const lastIdx = out.length - 1;
        if (
          lastIdx >= 0 &&
          !out[lastIdx].isFinal &&
          out[lastIdx].participantIdentity === chunk.participantIdentity
        ) {
          out[lastIdx] = {
            ...chunk,
            ...speaker,
            timestamp: formatChunkTime(chunk),
          };
        } else {
          out.push({
            ...chunk,
            ...speaker,
            timestamp: formatChunkTime(chunk),
          });
        }
      }
    }
    return out;
  }, [chunks]);

  // Legacy mock removed (TV-P7-06). The transcription array above
  // is now built from `chunks` via useTranscriptStream. The original
  // hardcoded Dr. Reddy / Maria dialogue is in git history if anyone
  // ever wants the demo copy back.

  // SOAP note data
  const soapNote = {
    subjective: {
      tag: 'S',
      title: 'Subjective',
      color: 'bg-blue-500/20 text-blue-400',
      content:
        'Patient reports ongoing difficulty adhering to recommended diet despite awareness of disease. Expresses concern about medication adherence and potential side effects. Motivated to improve health. Acknowledges previous successful periods with dietary compliance.',
    },
    objective: {
      tag: 'O',
      title: 'Objective',
      color: 'bg-green-500/20 text-green-400',
      content:
        'Vital signs stable. Recent HbA1c 8.2% (previous 8.5%), indicating slight improvement but not at goal of <7%. Weight 185 lbs. BP 138/88 mmHg, elevated. Last eye exam 14 months ago (overdue). Comprehensive metabolic panel within normal limits. Current medications: Metformin 1000mg BID.',
    },
    assessment: {
      tag: 'A',
      title: 'Assessment',
      color: 'bg-amber-500/20 text-amber-400',
      content:
        'Type 2 Diabetes Mellitus inadequately controlled on current regimen. Essential hypertension, Stage 1, uncontrolled. Overweight with BMI 32. Medication non-adherence risk. Preventive care overdue (diabetic retinopathy screening).',
    },
    plan: {
      tag: 'P',
      title: 'Plan',
      color: 'bg-purple-500/20 text-purple-400',
      content:
        'Initiate GLP-1 agonist (Semaglutide) 0.5mg SC weekly for glycemic control and weight management. Reinforce dietary counseling with referral to registered dietitian. Order HbA1c and comprehensive metabolic panel in 3 months. Referral to ophthalmology for urgent dilated eye exam. Increase lisinopril to 20mg daily for blood pressure management. Schedule follow-up telehealth visit in 4 weeks.',
    },
  };

  // AI Suggested Codes
  const suggestedCodes = {
    icd10: ['E11.65 - Type 2 diabetes with hypoglycemia', 'I10 - Essential hypertension', 'E66.01 - Obesity with excess calories'],
    cpt: ['99214 - Office visit, established patient, moderate-high complexity'],
    hcc: ['HCC 19 - Diabetes with complications'],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0d14] to-[#0f1119] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">AI Ambient Documentation</h1>
          <p className="text-gray-400">Dr. Priya Reddy • Visit on Mar 21, 2026 at 10:02 AM</p>
        </div>

        {/* Main Content - Split Layout */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Left Column - Transcription */}
          <div>
            <Card className="p-6 h-full flex flex-col">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-white mb-2">Live Transcription</h2>
                <p className="text-sm text-gray-400">Speaker-labeled conversation</p>
              </div>

              {/* Transcription Messages — TV-P7-06 reads from Firestore */}
              <div className="flex-grow space-y-4 mb-6 overflow-y-auto max-h-96">
                {error && (
                  <div className="text-xs text-red-400 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                    Failed to load transcript: {error.message}
                  </div>
                )}
                {!error && transcription.length === 0 && (
                  <div className="text-xs text-gray-500 italic text-center py-8">
                    Waiting for audio… transcription appears here once the
                    visit's audio reaches the STT bridge.
                  </div>
                )}
                {transcription.map((item) => (
                  <div key={item.id} className="flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-semibold ${item.color}`}>
                        {item.label}
                      </span>
                      <span className="text-xs text-gray-500">{item.timestamp}</span>
                      {item.languageCode && item.languageCode !== 'en-IN' && (
                        <span className="text-[10px] text-gray-500 uppercase">
                          {item.languageCode}
                        </span>
                      )}
                      {!item.isFinal && (
                        <span className="text-[10px] text-gray-500 italic">
                          (interim)
                        </span>
                      )}
                    </div>
                    <div
                      className={`${item.bgColor} p-3 rounded-lg border border-[#1e2235] ${item.isFinal ? '' : 'opacity-60'}`}
                    >
                      <p className="text-sm text-gray-200">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Listening Indicator */}
              <div className="pt-4 border-t border-[#1e2235] flex items-center gap-3">
                <div className="relative">
                  <Mic className={`w-5 h-5 ${pulseVisible ? 'text-red-500' : 'text-red-400'}`} />
                  {pulseVisible && (
                    <div className="absolute inset-0 animate-pulse">
                      <Mic className="w-5 h-5 text-red-500" />
                    </div>
                  )}
                </div>
                <span className="text-sm font-medium text-gray-300">Listening...</span>
              </div>
            </Card>
          </div>

          {/* Right Column - SOAP Note */}
          <div>
            <Card className="p-6 h-full flex flex-col">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-white mb-2">Auto-Generated SOAP Note</h2>
                <p className="text-sm text-gray-400">Clinical documentation</p>
              </div>

              {/* SOAP Sections */}
              <div className="flex-grow space-y-4 overflow-y-auto max-h-96 mb-6">
                {[soapNote.subjective, soapNote.objective, soapNote.assessment, soapNote.plan].map((section, idx) => (
                  <div key={idx} className="bg-[#12141e] rounded-lg border border-[#1e2235] overflow-hidden">
                    <div className={`${section.color} px-3 py-2 font-semibold text-sm flex items-center gap-2`}>
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-white/10 rounded text-xs font-bold">
                        {section.tag}
                      </span>
                      {section.title}
                    </div>
                    <p className="px-4 py-3 text-sm text-gray-300 leading-relaxed">{section.content}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>

        {/* AI Suggested Codes Card */}
        <Card className="p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">AI Suggested Codes</h3>

          <div className="grid grid-cols-3 gap-6">
            {/* ICD-10 */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-3">ICD-10 Diagnoses</p>
              <div className="space-y-2">
                {suggestedCodes.icd10.map((code, idx) => (
                  <div key={idx} className="p-3 bg-[#12141e] rounded-lg border border-[#1e2235] hover:border-blue-500/50 transition-colors cursor-pointer">
                    <p className="text-sm font-mono text-blue-400">{code}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CPT */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-3">CPT Procedure</p>
              <div className="space-y-2">
                {suggestedCodes.cpt.map((code, idx) => (
                  <div key={idx} className="p-3 bg-[#12141e] rounded-lg border border-[#1e2235] hover:border-green-500/50 transition-colors cursor-pointer">
                    <p className="text-sm font-mono text-green-400">{code}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* HCC */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-3">HCC Codes</p>
              <div className="space-y-2">
                {suggestedCodes.hcc.map((code, idx) => (
                  <div key={idx} className="p-3 bg-[#12141e] rounded-lg border border-[#1e2235] hover:border-purple-500/50 transition-colors cursor-pointer">
                    <p className="text-sm font-mono text-purple-400">{code}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button className="px-6 py-3 bg-[#12141e] hover:bg-[#1e2235] text-gray-100 font-semibold rounded-lg border border-[#1e2235] transition-colors flex items-center gap-2">
            <Edit2 className="w-5 h-5" />
            Edit
          </button>
          <button className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            Sign & Finalize
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIDocs;
